// Inventory state management hook
// Generic inventory system that works across domains (kitchen, rooms, health, etc.)
// Follows same pattern as useTasks for consistency

import { useState, useEffect, useCallback, useRef } from 'react';

// Default shelf life in days by category (used when no expiry date is entered)
export const DEFAULT_SHELF_LIFE = {
  protein: 4,      // Fresh meat/fish - 3-5 days
  dairy: 7,        // Milk, yogurt - ~1 week
  veg: 6,          // Fresh vegetables - 5-7 days
  fruit: 6,        // Fresh fruit - 5-7 days
  carb: 14,        // Bread, pasta - ~2 weeks
  condiment: 90,   // Sauces, oils - ~3 months
  beverage: 30,    // Drinks - ~1 month
  other: 14        // Default - 2 weeks
};

// Compute freshness status based on dates
export function computeFreshness(item) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let expiryDate = null;
  
  if (item.expiryDate) {
    // Use explicit expiry date
    expiryDate = new Date(item.expiryDate);
  } else if (item.purchaseDate) {
    // Estimate from purchase date + default shelf life
    const shelfLife = DEFAULT_SHELF_LIFE[item.category] || DEFAULT_SHELF_LIFE.other;
    expiryDate = new Date(item.purchaseDate);
    expiryDate.setDate(expiryDate.getDate() + shelfLife);
  } else {
    // No date info - can't compute freshness
    return { status: 'unknown', daysLeft: null, expiryDate: null };
  }
  
  expiryDate.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
  
  let status;
  if (daysLeft <= 0) {
    status = 'expired';
  } else if (daysLeft <= 2) {
    status = 'expiring';  // 0-2 days - use ASAP
  } else if (daysLeft <= 5) {
    status = 'use-soon';  // 3-5 days - plan to use
  } else {
    status = 'fresh';     // 6+ days - no rush
  }
  
  return { 
    status, 
    daysLeft, 
    expiryDate: expiryDate.toISOString().split('T')[0]
  };
}

// Domain-specific category configurations
export const INVENTORY_CATEGORIES = {
  kitchen: ['protein', 'carb', 'veg', 'fruit', 'dairy', 'condiment', 'beverage', 'other'],
  rooms: ['furniture', 'decor', 'storage', 'electronics', 'clothing', 'documents', 'other'],
  health: ['medication', 'supplement', 'equipment', 'supplies', 'other'],
  kids: ['school', 'clothing', 'toys', 'documents', 'other']
};

// Status options for inventory items
export const INVENTORY_STATUS = {
  stocked: 'stocked',     // Have it
  low: 'low',             // Running low
  out: 'out',             // Used up (removed from active inventory)
  needToBuy: 'need_to_buy', // On shopping list
  bought: 'bought'        // Recently purchased (for undo)
};

const STORAGE_KEY = 'mabel-inventory';

export default function useInventory(domain = 'kitchen') {
  // ============ STATE ============

  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Track if initial load is done
  const initialLoadDone = useRef(false);

  // ============ EFFECTS ============

  // Initial load from localStorage
  useEffect(() => {
    const loadInventory = () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            setInventory(parsed);
          }
        } catch (e) {
          console.error('Failed to load inventory:', e);
        }
      }
      initialLoadDone.current = true;
      setIsLoading(false);
    };

    loadInventory();
  }, []);

  // Save to localStorage whenever inventory changes
  useEffect(() => {
    if (!initialLoadDone.current) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(inventory));
  }, [inventory]);

  // ============ COMPUTED VALUES ============

  // Filter inventory by current domain
  const domainInventory = inventory.filter(item => item.domain === domain);

  // Items that are in stock (stocked or low)
  const stockedItems = domainInventory.filter(
    item => item.status === INVENTORY_STATUS.stocked || item.status === INVENTORY_STATUS.low
  );

  // Items on the shopping list
  const shoppingList = domainInventory.filter(
    item => item.status === INVENTORY_STATUS.needToBuy
  );

  // Recently bought items (for undo)
  const boughtItems = domainInventory.filter(
    item => item.status === INVENTORY_STATUS.bought
  );

  // Group by category
  const itemsByCategory = stockedItems.reduce((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  // Get categories for current domain
  const categories = INVENTORY_CATEGORIES[domain] || INVENTORY_CATEGORIES.kitchen;

  // ============ HANDLERS ============

  const addItem = useCallback((itemData) => {
    const newItem = {
      id: itemData.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: itemData.name?.trim() || 'Unknown item',
      domain: itemData.domain || domain,
      category: itemData.category || 'other',
      quantity: itemData.quantity || '',
      status: itemData.status || INVENTORY_STATUS.stocked,
      favorite: itemData.favorite || false,
      notes: itemData.notes || '',
      // Freshness tracking fields
      purchaseDate: itemData.purchaseDate || new Date().toISOString().split('T')[0],
      expiryDate: itemData.expiryDate || null,
      openedDate: itemData.openedDate || null,
      createdAt: itemData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    setInventory(prev => [...prev, newItem]);
    return newItem;
  }, [domain]);

  const addItems = useCallback((items) => {
    const newItems = items.map(itemData => ({
      id: itemData.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: itemData.name?.trim() || 'Unknown item',
      domain: itemData.domain || domain,
      category: itemData.category || 'other',
      quantity: itemData.quantity || '',
      status: itemData.status || INVENTORY_STATUS.stocked,
      favorite: itemData.favorite || false,
      notes: itemData.notes || '',
      // Freshness tracking fields
      purchaseDate: itemData.purchaseDate || new Date().toISOString().split('T')[0],
      expiryDate: itemData.expiryDate || null,
      openedDate: itemData.openedDate || null,
      createdAt: itemData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }));

    setInventory(prev => [...prev, ...newItems]);
    return newItems;
  }, [domain]);

  const updateItem = useCallback((itemId, updates) => {
    setInventory(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          ...updates,
          updatedAt: new Date().toISOString()
        };
      }
      return item;
    }));
  }, []);

  const removeItem = useCallback((itemId) => {
    setInventory(prev => prev.filter(item => item.id !== itemId));
  }, []);

  // Mark as ran out - moves to "out" status (can be re-added to shopping)
  const markAsOut = useCallback((itemId) => {
    updateItem(itemId, { status: INVENTORY_STATUS.out });
  }, [updateItem]);

  // Mark as low - still have some but need more
  const markAsLow = useCallback((itemId) => {
    updateItem(itemId, { status: INVENTORY_STATUS.low });
  }, [updateItem]);

  // Add to shopping list
  const addToShoppingList = useCallback((itemId) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;

    // If item is stocked, mark as low and add a shopping entry
    if (item.status === INVENTORY_STATUS.stocked) {
      updateItem(itemId, { status: INVENTORY_STATUS.low });
    }

    // Check if already on shopping list
    const existingShoppingItem = inventory.find(
      i => i.name.toLowerCase() === item.name.toLowerCase() &&
           i.status === INVENTORY_STATUS.needToBuy &&
           i.domain === item.domain
    );

    if (!existingShoppingItem) {
      addItem({
        name: item.name,
        domain: item.domain,
        category: item.category,
        status: INVENTORY_STATUS.needToBuy
      });
    }
  }, [inventory, updateItem, addItem]);

  // Mark shopping item as bought - changes to bought status, adds to inventory
  const markAsBought = useCallback((itemId) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;

    // Change shopping item to bought status (keeps it for undo)
    updateItem(itemId, { 
      status: INVENTORY_STATUS.bought,
      boughtAt: new Date().toISOString()
    });

    // Find existing inventory item with same name or add new
    const existingItem = inventory.find(
      i => i.name.toLowerCase() === item.name.toLowerCase() &&
           i.status !== INVENTORY_STATUS.needToBuy &&
           i.status !== INVENTORY_STATUS.bought &&
           i.domain === item.domain
    );

    if (existingItem) {
      updateItem(existingItem.id, { status: INVENTORY_STATUS.stocked });
    } else {
      addItem({
        name: item.name,
        domain: item.domain,
        category: item.category,
        status: INVENTORY_STATUS.stocked
      });
    }
  }, [inventory, updateItem, addItem]);

  // Undo bought - moves item back to shopping list
  const undoBought = useCallback((itemId) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;

    // Change back to need_to_buy status
    updateItem(itemId, { 
      status: INVENTORY_STATUS.needToBuy,
      boughtAt: null
    });

    // Find and remove/revert the pantry item that was added
    const pantryItem = inventory.find(
      i => i.name.toLowerCase() === item.name.toLowerCase() &&
           i.status === INVENTORY_STATUS.stocked &&
           i.domain === item.domain &&
           i.id !== itemId
    );

    if (pantryItem) {
      // Check if it existed before (has older createdAt) or was just added
      const itemBoughtAt = item.boughtAt ? new Date(item.boughtAt) : new Date();
      const pantryCreatedAt = new Date(pantryItem.createdAt);
      
      if (pantryCreatedAt >= itemBoughtAt) {
        // Was just added, remove it
        removeItem(pantryItem.id);
      }
      // If it existed before, leave it as is
    }
  }, [inventory, updateItem, removeItem]);

  // Toggle favorite
  const toggleFavorite = useCallback((itemId) => {
    const item = inventory.find(i => i.id === itemId);
    if (item) {
      updateItem(itemId, { favorite: !item.favorite });
    }
  }, [inventory, updateItem]);

  // Search inventory
  const searchItems = useCallback((query) => {
    if (!query?.trim()) return stockedItems;

    const lowerQuery = query.toLowerCase();
    return stockedItems.filter(item =>
      item.name.toLowerCase().includes(lowerQuery) ||
      item.category.toLowerCase().includes(lowerQuery) ||
      (item.quantity && item.quantity.toLowerCase().includes(lowerQuery))
    );
  }, [stockedItems]);

  // Get all items (across all domains) - useful for Mabel context
  const getAllItems = useCallback(() => inventory, [inventory]);

  // Clear all items for a domain
  const clearDomain = useCallback((targetDomain = domain) => {
    setInventory(prev => prev.filter(item => item.domain !== targetDomain));
  }, [domain]);

  // ============ RETURN ============

  return {
    // State
    inventory: domainInventory,
    allInventory: inventory,
    stockedItems,
    shoppingList,
    boughtItems,
    itemsByCategory,
    categories,
    isLoading,

    // Handlers
    addItem,
    addItems,
    updateItem,
    removeItem,
    markAsOut,
    markAsLow,
    addToShoppingList,
    markAsBought,
    undoBought,
    toggleFavorite,
    searchItems,
    getAllItems,
    clearDomain
  };
}
